/**
 * Publisher payout overrides — operator UI (FS-821 dash page).
 *
 * UX intent:
 *   - Managers tune per-affiliate x offer payout rules without editing base
 *     campaign settings.
 *   - The list is scoped to one affiliate at a time (backend lists by
 *     affiliate_id). A toolbar lets operators narrow further by offer id and
 *     status chips (scope / age).
 *   - "New Override" opens a right-anchored slide-over drawer with a form for
 *     payout type, amount or rate, currency, offer id.
 *   - Rows carry scope + age badges and support bulk-delete via checkboxes.
 *
 * API shape (`apps/api/src/routes/publisher-payout-overrides.ts`):
 *   - `cpa | cps | revshare` payout types (no CPC/CPM).
 *   - No persisted effective-date range — status badges are derived from
 *     `offer_id` presence + `created_at` recency.
 *
 * Design: emerald -> sky headline, zinc borders, 6-8px rounding, light-only,
 * no emojis, no competitor names. Plain HTML table (no @tanstack/react-table
 * dep, which is not installed in the dash workspace).
 */
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import {
	applyFilters,
	computeAge,
	computeScope,
	describeAge,
	describePayoutType,
	describeScope,
	emptyDraft,
	formatCreatedAt,
	formatPayout,
	isPayoutType,
	overrideQueryKeys,
	PAYOUT_TYPES,
	validateDraft,
	type DraftOverride,
	type OverrideAge,
	type OverrideRow,
	type OverrideScope,
	type PayoutType,
} from "./api";
import {
	usePublisherPayoutOverridesBulkDelete,
	usePublisherPayoutOverridesCreate,
	usePublisherPayoutOverridesDelete,
	usePublisherPayoutOverridesList,
} from "./hooks";

// Silence the unused-import check for `overrideQueryKeys` — exported for
// downstream test harnesses that mimic invalidation manually.
void overrideQueryKeys;

// ---------------------------------------------------------------------------
// Tailwind token bundles — keep JSX lean.
// ---------------------------------------------------------------------------

const CARD = "rounded-[8px] border border-zinc-200 bg-white shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const SELECT = INPUT;
const LABEL = "block text-sm font-medium text-zinc-700 mb-1";
const BTN_PRIMARY =
	"rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50";
const BTN_GRADIENT =
	"rounded-md bg-gradient-to-r from-emerald-500 to-sky-500 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50";
const BTN_SECONDARY =
	"rounded-md border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50";
const BTN_DANGER =
	"rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50";
const BADGE =
	"inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
const BADGE_EMERALD = `${BADGE} bg-emerald-50 text-emerald-700`;
const BADGE_SKY = `${BADGE} bg-sky-50 text-sky-700`;
const BADGE_AMBER = `${BADGE} bg-amber-50 text-amber-700`;
const BADGE_ZINC = `${BADGE} bg-zinc-100 text-zinc-600`;

// ---------------------------------------------------------------------------
// Public entry — default export for the route file.
// ---------------------------------------------------------------------------

export interface PublisherPayoutOverridesPageProps {
	/** Route search param `?affiliate=` is passed through here. */
	initialAffiliateId?: string;
}

export function PublisherPayoutOverridesPage({
	initialAffiliateId,
}: PublisherPayoutOverridesPageProps) {
	// Affiliate picker state ----------------------------------------------------
	const [draftAffiliate, setDraftAffiliate] = useState(initialAffiliateId ?? "");
	const [affiliateId, setAffiliateId] = useState(initialAffiliateId ?? "");

	// Filters -------------------------------------------------------------------
	const [offerQuery, setOfferQuery] = useState("");
	const [scopeFilter, setScopeFilter] = useState<OverrideScope | "all">("all");
	const [ageFilter, setAgeFilter] = useState<OverrideAge | "all">("all");

	// Row selection -------------------------------------------------------------
	const [selection, setSelection] = useState<Record<string, boolean>>({});

	// Drawer --------------------------------------------------------------------
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [draft, setDraft] = useState<DraftOverride>(
		emptyDraft(initialAffiliateId ?? ""),
	);
	const [draftError, setDraftError] = useState<string | null>(null);

	// Data hooks ----------------------------------------------------------------
	const query = usePublisherPayoutOverridesList({ affiliateId });
	const createMut = usePublisherPayoutOverridesCreate({
		onSuccess: () => {
			toast.success("Override saved");
			setDrawerOpen(false);
			setDraft(emptyDraft(affiliateId));
			setDraftError(null);
		},
		onError: (e) => {
			setDraftError(e.message);
		},
	});
	const deleteMut = usePublisherPayoutOverridesDelete({
		onSuccess: () => {
			toast.success("Override deleted");
		},
		onError: (e) => {
			toast.error(e.message);
		},
	});
	const bulkMut = usePublisherPayoutOverridesBulkDelete({
		onSuccess: (res) => {
			if (res.failed.length > 0) {
				toast.error(
					`${res.succeeded.length} deleted, ${res.failed.length} failed`,
				);
			} else {
				toast.success(`${res.succeeded.length} overrides deleted`);
			}
			setSelection({});
		},
	});

	// Derived rows --------------------------------------------------------------
	const rows = query.data?.overrides ?? [];
	const filtered = useMemo(
		() =>
			applyFilters(rows, {
				offerQuery,
				scope: scopeFilter,
				age: ageFilter,
			}),
		[rows, offerQuery, scopeFilter, ageFilter],
	);

	const selectedIds = useMemo(
		() => Object.entries(selection).filter(([, v]) => v).map(([k]) => k),
		[selection],
	);

	const onSubmitDraft = useCallback(() => {
		const v = validateDraft(draft);
		if (!v.ok || !v.payload) {
			setDraftError(v.error ?? "Invalid override");
			return;
		}
		createMut.mutate(v.payload);
	}, [draft, createMut]);

	const onLoadAffiliate = useCallback(() => {
		const id = draftAffiliate.trim();
		if (!id) return;
		setAffiliateId(id);
		setSelection({});
	}, [draftAffiliate]);

	const canSubmit = useMemo(() => validateDraft(draft).ok, [draft]);

	// ---------------------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------------------
	return (
		<div
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 pb-12 pt-6 sm:pt-8"
			data-testid="publisher-payout-overrides-page"
		>
			{/* Headline ------------------------------------------------------- */}
			<header className="flex flex-col gap-2">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent">
					Publisher Payout Overrides
				</h1>
				<p className="max-w-3xl text-sm text-zinc-500">
					Per-affiliate rules that override the base offer payout. Useful for
					VIP partners, renegotiated splits, or short-term promo rates. Rules
					are scoped to one affiliate at a time; offer scoping is optional.
				</p>
			</header>

			{/* Affiliate picker ----------------------------------------------- */}
			<section className={`${CARD} p-4 sm:p-5`}>
				<div className="flex flex-wrap items-end gap-3">
					<div className="flex-1 min-w-[200px]">
						<label className={LABEL} htmlFor="affiliate-filter-input">
							Affiliate ID
						</label>
						<input
							id="affiliate-filter-input"
							data-testid="affiliate-filter-input"
							className={INPUT}
							placeholder="aff_..."
							value={draftAffiliate}
							onChange={(e) => setDraftAffiliate(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") onLoadAffiliate();
							}}
						/>
					</div>
					<button
						type="button"
						data-testid="affiliate-filter-submit"
						className={BTN_PRIMARY}
						onClick={onLoadAffiliate}
					>
						Load overrides
					</button>
					<button
						type="button"
						data-testid="new-override-btn"
						className={BTN_GRADIENT}
						disabled={!affiliateId}
						onClick={() => {
							setDraft(emptyDraft(affiliateId));
							setDraftError(null);
							setDrawerOpen(true);
						}}
					>
						New Override
					</button>
				</div>
				{!affiliateId && (
					<p className="mt-3 text-sm text-zinc-500">
						Load overrides for an affiliate to begin. Deep-link with
						<code className="mx-1 rounded bg-zinc-100 px-1 py-0.5 text-xs text-zinc-700">
							?affiliate=aff_123
						</code>
						.
					</p>
				)}
			</section>

			{/* Toolbar -------------------------------------------------------- */}
			{affiliateId && (
				<section className={`${CARD} p-4 sm:p-5`}>
					<div className="flex flex-wrap items-end gap-3">
						<div className="flex-1 min-w-[200px]">
							<label className={LABEL} htmlFor="offer-filter">
								Offer filter
							</label>
							<input
								id="offer-filter"
								data-testid="offer-filter"
								className={INPUT}
								placeholder="offer_..."
								value={offerQuery}
								onChange={(e) => setOfferQuery(e.target.value)}
							/>
						</div>
						<div>
							<span className={LABEL}>Scope</span>
							<ChipRow
								value={scopeFilter}
								onChange={setScopeFilter}
								options={[
									{ value: "all", label: "All" },
									{ value: "global", label: "Global" },
									{ value: "offer-scoped", label: "Offer-scoped" },
								]}
							/>
						</div>
						<div>
							<span className={LABEL}>Age</span>
							<ChipRow
								value={ageFilter}
								onChange={setAgeFilter}
								options={[
									{ value: "all", label: "All" },
									{ value: "recent", label: "Recent" },
									{ value: "aged", label: "Aged" },
								]}
							/>
						</div>
					</div>
				</section>
			)}

			{/* Table ---------------------------------------------------------- */}
			{affiliateId && (
				<section className={CARD}>
					<header className="flex items-center justify-between border-b border-zinc-100 px-4 py-3 sm:px-5">
						<div>
							<h2 className="text-sm font-semibold text-zinc-700">
								Overrides for {affiliateId}
							</h2>
							<p className="text-xs text-zinc-500">
								{query.isLoading
									? "Loading..."
									: `${filtered.length} of ${rows.length} shown`}
							</p>
						</div>
					</header>

					{query.isLoading && (
						<div className="p-8 text-center text-sm text-zinc-500">
							Loading overrides...
						</div>
					)}
					{query.isError && (
						<div className="p-8 text-center text-sm text-red-600">
							Failed to load: {query.error?.message ?? "unknown error"}
						</div>
					)}
					{!query.isLoading && !query.isError && filtered.length === 0 && (
						<div
							className="flex flex-col items-center gap-3 p-10 text-center"
							data-testid="empty-state"
						>
							<p className="text-sm text-zinc-500">
								No overrides yet for this affiliate.
							</p>
							<button
								type="button"
								data-testid="empty-new-btn"
								className={BTN_PRIMARY}
								onClick={() => {
									setDraft(emptyDraft(affiliateId));
									setDraftError(null);
									setDrawerOpen(true);
								}}
							>
								Create the first one
							</button>
						</div>
					)}
					{!query.isLoading && !query.isError && filtered.length > 0 && (
						<div className="overflow-x-auto">
							<table
								className="w-full text-sm"
								data-testid="overrides-table"
							>
								<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
									<tr>
										<th className="px-4 py-2">
											<input
												type="checkbox"
												aria-label="Select all"
												checked={
													filtered.length > 0 &&
													filtered.every((r) => selection[r.id])
												}
												onChange={(e) => {
													const next: Record<string, boolean> = {};
													if (e.target.checked) {
														for (const r of filtered) next[r.id] = true;
													}
													setSelection(next);
												}}
											/>
										</th>
										<th className="px-4 py-2">Offer</th>
										<th className="px-4 py-2">Payout</th>
										<th className="px-4 py-2">Payout type</th>
										<th className="px-4 py-2">Scope</th>
										<th className="px-4 py-2">Age</th>
										<th className="px-4 py-2">Created</th>
										<th className="px-4 py-2 text-right">Actions</th>
									</tr>
								</thead>
								<tbody>
									{filtered.map((row) => (
										<OverrideTableRow
											key={row.id}
											row={row}
											selected={!!selection[row.id]}
											onToggle={() =>
												setSelection((s) => ({ ...s, [row.id]: !s[row.id] }))
											}
											onDelete={() => deleteMut.mutate(row.id)}
										/>
									))}
								</tbody>
							</table>
						</div>
					)}
				</section>
			)}

			{/* Bulk-delete bar ------------------------------------------------ */}
			{selectedIds.length > 0 && (
				<div
					className="fixed bottom-4 left-1/2 z-30 -translate-x-1/2 transform"
					data-testid="bulk-bar"
				>
					<div
						className={`${CARD} flex items-center gap-3 rounded-full px-4 py-2 shadow-lg`}
					>
						<span className="text-sm text-zinc-700">
							{selectedIds.length} selected
						</span>
						<button
							type="button"
							className={BTN_DANGER}
							data-testid="bulk-delete"
							disabled={bulkMut.isPending}
							onClick={() => bulkMut.mutate(selectedIds)}
						>
							Delete {selectedIds.length}
						</button>
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={() => setSelection({})}
						>
							Clear
						</button>
					</div>
				</div>
			)}

			{/* Drawer --------------------------------------------------------- */}
			{drawerOpen && (
				<NewOverrideDrawer
					draft={draft}
					setDraft={setDraft}
					onClose={() => setDrawerOpen(false)}
					onSubmit={onSubmitDraft}
					canSubmit={canSubmit}
					error={draftError}
					pending={createMut.isPending}
				/>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Chip row — tiny filter-chip component.
// ---------------------------------------------------------------------------

interface ChipOption<T extends string> {
	value: T;
	label: string;
}

interface ChipRowProps<T extends string> {
	value: T;
	options: readonly ChipOption<T>[];
	onChange: (v: T) => void;
}

function ChipRow<T extends string>({ value, options, onChange }: ChipRowProps<T>) {
	return (
		<div className="flex flex-wrap gap-1">
			{options.map((o) => {
				const active = o.value === value;
				return (
					<button
						key={o.value}
						type="button"
						className={`rounded-full px-3 py-1 text-xs font-medium transition ${
							active
								? "bg-emerald-500 text-white"
								: "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
						}`}
						onClick={() => onChange(o.value)}
					>
						{o.label}
					</button>
				);
			})}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Table row.
// ---------------------------------------------------------------------------

interface OverrideTableRowProps {
	row: OverrideRow;
	selected: boolean;
	onToggle: () => void;
	onDelete: () => void;
}

function OverrideTableRow({
	row,
	selected,
	onToggle,
	onDelete,
}: OverrideTableRowProps) {
	const scope = computeScope(row);
	const age = computeAge(row);
	return (
		<tr
			className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50"
			data-testid={`override-row-${row.id}`}
		>
			<td className="px-4 py-3">
				<input
					type="checkbox"
					data-testid={`select-${row.id}`}
					checked={selected}
					onChange={onToggle}
					aria-label={`Select override ${row.id}`}
				/>
			</td>
			<td className="px-4 py-3 text-zinc-700">
				{row.offer_id ?? (
					<span className="text-zinc-400">—</span>
				)}
			</td>
			<td className="px-4 py-3 font-medium text-zinc-900">
				{formatPayout(row)}
			</td>
			<td className="px-4 py-3">
				<span className={BADGE_ZINC}>{describePayoutType(row.payout_type)}</span>
			</td>
			<td className="px-4 py-3">
				<span
					data-testid={`scope-badge-${row.id}`}
					className={scope === "global" ? BADGE_EMERALD : BADGE_SKY}
				>
					{describeScope(scope)}
				</span>
			</td>
			<td className="px-4 py-3">
				<span
					data-testid={`age-badge-${row.id}`}
					className={age === "recent" ? BADGE_AMBER : BADGE_ZINC}
				>
					{describeAge(age)}
				</span>
			</td>
			<td className="px-4 py-3 text-zinc-500">
				{formatCreatedAt(row.created_at)}
			</td>
			<td className="px-4 py-3 text-right">
				<button
					type="button"
					className={BTN_DANGER}
					data-testid={`delete-${row.id}`}
					onClick={() => {
						if (confirm("Delete this override?")) onDelete();
					}}
				>
					Delete
				</button>
			</td>
		</tr>
	);
}

// ---------------------------------------------------------------------------
// Drawer.
// ---------------------------------------------------------------------------

interface NewOverrideDrawerProps {
	draft: DraftOverride;
	setDraft: (d: DraftOverride) => void;
	onClose: () => void;
	onSubmit: () => void;
	canSubmit: boolean;
	error: string | null;
	pending: boolean;
}

function NewOverrideDrawer({
	draft,
	setDraft,
	onClose,
	onSubmit,
	canSubmit,
	error,
	pending,
}: NewOverrideDrawerProps) {
	return (
		<div className="fixed inset-0 z-40" data-testid="override-drawer">
			<button
				type="button"
				aria-label="Close drawer"
				className="fixed inset-0 bg-zinc-900/30"
				onClick={onClose}
			/>
			<aside className="fixed right-0 top-0 flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-zinc-200 bg-white shadow-2xl">
				<header className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
					<div>
						<h2 className="text-lg font-semibold text-zinc-900">
							New payout override
						</h2>
						<p className="text-xs text-zinc-500">
							Affiliate: <strong>{draft.affiliate_id}</strong>
						</p>
					</div>
					<button
						type="button"
						className={BTN_SECONDARY}
						data-testid="drawer-close"
						onClick={onClose}
					>
						Close
					</button>
				</header>
				<form
					className="flex flex-1 flex-col gap-4 px-5 py-4"
					onSubmit={(e) => {
						e.preventDefault();
						onSubmit();
					}}
				>
					<div>
						<label className={LABEL} htmlFor="drawer-affiliate">
							Affiliate ID
						</label>
						<input
							id="drawer-affiliate"
							data-testid="drawer-affiliate"
							className={INPUT}
							value={draft.affiliate_id}
							onChange={(e) =>
								setDraft({ ...draft, affiliate_id: e.target.value })
							}
						/>
					</div>
					<div>
						<label className={LABEL} htmlFor="drawer-offer">
							Offer ID <span className="text-zinc-400">(optional)</span>
						</label>
						<input
							id="drawer-offer"
							data-testid="drawer-offer"
							className={INPUT}
							placeholder="Leave empty for global rule"
							value={draft.offer_id}
							onChange={(e) => setDraft({ ...draft, offer_id: e.target.value })}
						/>
					</div>
					<div>
						<span className={LABEL}>Payout type</span>
						<div className="flex flex-wrap gap-2">
							{PAYOUT_TYPES.map((t) => {
								const active = draft.payout_type === t;
								return (
									<label
										key={t}
										className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${
											active
												? "border-emerald-500 bg-emerald-50 text-emerald-700"
												: "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
										}`}
									>
										<input
											type="radio"
											name="payout-type"
											value={t}
											checked={active}
											onChange={(e) => {
												if (isPayoutType(e.target.value)) {
													setDraft({ ...draft, payout_type: e.target.value });
												}
											}}
											data-testid={`drawer-type-${t}`}
										/>
										{describePayoutType(t as PayoutType)}
									</label>
								);
							})}
						</div>
					</div>
					{draft.payout_type !== "revshare" ? (
						<div>
							<label className={LABEL} htmlFor="drawer-amount">
								Amount (dollars)
							</label>
							<input
								id="drawer-amount"
								data-testid="drawer-amount"
								className={INPUT}
								placeholder="5.00"
								inputMode="decimal"
								value={draft.amount_display}
								onChange={(e) =>
									setDraft({ ...draft, amount_display: e.target.value })
								}
							/>
						</div>
					) : (
						<div>
							<label className={LABEL} htmlFor="drawer-rate">
								Rate (%)
							</label>
							<input
								id="drawer-rate"
								data-testid="drawer-rate"
								className={INPUT}
								placeholder="15.00"
								inputMode="decimal"
								value={draft.rate_pct_display}
								onChange={(e) =>
									setDraft({ ...draft, rate_pct_display: e.target.value })
								}
							/>
						</div>
					)}
					<div>
						<label className={LABEL} htmlFor="drawer-currency">
							Currency
						</label>
						<select
							id="drawer-currency"
							data-testid="drawer-currency"
							className={SELECT}
							value={draft.currency}
							onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
						>
							<option value="USD">USD</option>
							<option value="EUR">EUR</option>
							<option value="GBP">GBP</option>
							<option value="JPY">JPY</option>
							<option value="CAD">CAD</option>
							<option value="AUD">AUD</option>
						</select>
					</div>
					{error && (
						<div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
							{error}
						</div>
					)}
					<div className="mt-auto flex items-center justify-end gap-2 border-t border-zinc-100 pt-4">
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={onClose}
						>
							Cancel
						</button>
						<button
							type="submit"
							className={BTN_GRADIENT}
							data-testid="drawer-submit"
							disabled={!canSubmit || pending}
						>
							{pending ? "Saving..." : "Save override"}
						</button>
					</div>
				</form>
			</aside>
		</div>
	);
}

export default PublisherPayoutOverridesPage;
